import { useState } from 'react';

/**
 * Per-device clock format preference — 12-hour ("8:24 AM") vs 24-hour
 * ("20:24"). Applied everywhere times of day are shown (food log, timeline,
 * sleep, meal detail) via the `formatTime` / `formatClock` helpers in
 * `lib/formatters.ts`, which read the stored value directly. Saved like the
 * theme / font / thumbnail prefs; toggled on the Profile page.
 *
 * Read-on-mount is enough: the toggle lives on Profile and patient views
 * remount when the clinician returns to them, so they pick up the current value.
 */
export type TimeFormat = '12h' | '24h';

const STORAGE_KEY = 'ui.timeFormat';
const DEFAULT_FORMAT: TimeFormat = '12h';

export const readTimeFormat = (): TimeFormat => {
  if (typeof window === 'undefined') return DEFAULT_FORMAT;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '24h' ? '24h' : DEFAULT_FORMAT;
  } catch {
    return DEFAULT_FORMAT;
  }
};

export const useTimeFormat = (): [TimeFormat, (next: TimeFormat) => void] => {
  const [value, setValue] = useState<TimeFormat>(readTimeFormat);
  const set = (next: TimeFormat) => {
    setValue(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode / quota — ignore */
    }
  };
  return [value, set];
};
